import type React from "react"
import { createContext, useContext, useState, useEffect } from "react"
import type { RBACContextType, Role, ResourceType } from "@/lib/rbac/types"
import { RBAC_CONFIG } from "@/lib/rbac/constants"
import { useAuth } from "@/hooks/useAuth"
import { useRoleOverride } from "@/contexts/RoleOverrideContext"

// Create RBAC context
const RBACContext = createContext<RBACContextType | undefined>(undefined)

export const RBACProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true)
  const [roles, setRoles] = useState<Role[]>([])
  const { user } = useAuth()
  const { effectiveRole, isRoleOverridden } = useRoleOverride()

  useEffect(() => {
    if (!user) {
      setRoles([])
      setIsLoading(true)
      return
    }

    // Use effective role from RoleOverrideProvider (or original user role if not overridden)
    const roleToUse = effectiveRole || user.role || "guest"

    const userRole = RBAC_CONFIG.roles[roleToUse]
    if (!userRole) {
      console.error(`[RBAC] User role '${roleToUse}' not found in RBAC_CONFIG.roles`)
      setRoles([])
    } else {
      setRoles([userRole])
    }
    setIsLoading(false)
  }, [user, effectiveRole, isRoleOverridden])

  // Check if user has a specific role (using effective role)
  const hasRole = (roleId: string): boolean => {
    if (!user) return false
    return effectiveRole === roleId
  }

  // Check if user has permission for a resource
  const hasPermission = (resourceType: ResourceType, resource: string, action?: string): boolean => {
    if (!user) return false

    const roleToUse = effectiveRole || user.role

    // Check if user role exists in config
    if (!RBAC_CONFIG.roles[roleToUse]) {
      console.warn(`[RBAC] Role '${roleToUse}' not found in RBAC_CONFIG.roles`)
      return false
    }


    // Get default access policy based on resource type
    let defaultAccess = false
    switch (resourceType) {
      case "page":
        defaultAccess = RBAC_CONFIG.defaultPageAccess === "allow"
        break
      case "component":
        defaultAccess = RBAC_CONFIG.defaultComponentAccess === "allow"
        break
      case "attribute":
        defaultAccess = RBAC_CONFIG.defaultAttributeAccess === "allow"
        break
    }

    // If no roles, use default access
    if (roles.length === 0) return defaultAccess

    // Check if any role has the permission
    for (const role of roles) {
      // Skip undefined or invalid roles
      if (!role || !role.permissions) {
        // console.warn('[RBAC] Encountered invalid role in roles array:', role)
        continue
      }
      // For pages, we need explicit permission
      if (resourceType === "page") {
        const hasPagePermission = role.permissions.some(
          (permission) =>
            permission.resourceType === resourceType &&
            permission.resource === resource &&
            (!action || permission.action === action),
        )

        if (hasPagePermission) {
          return true
        }
      }
      // For components and attributes, check if permission is allowed
      else {
        const isAllowed = role.permissions.some(
          (permission) =>
            permission.resourceType === resourceType &&
            permission.resource === resource &&
            (!action || permission.action === action),
        )
        // If explicitly allowed in any role, allow access
        if (isAllowed) {
          return true
        }
      }
    }


    // For pages, default is deny unless explicitly allowed
    if (resourceType === "page") return false

    // For components and attributes, default is allow unless explicitly denied
    return defaultAccess
  }

  return (
    <RBACContext.Provider value={{ user, isLoading, hasPermission, hasRole, roles }}>{children}</RBACContext.Provider>
  )
}

// Hook to use RBAC context
export const useRBAC = (): RBACContextType => {
  const context = useContext(RBACContext)
  if (context === undefined) {
    throw new Error("useRBAC must be used within an RBACProvider")
  }
  return context
}
